<script lang="ts">
  import type { SurfaceSnapshot, SurfaceProject, SurfaceTask } from "../lib/types";
  import SourceBadges from "../lib/SourceBadges.svelte";

  let { data, onproject }: { data: SurfaceSnapshot; onproject: (p: SurfaceProject) => void } = $props();

  let query = $state("");

  function score(needle: string, haystack: string): number {
    if (!needle) return 0;
    const n = needle.toLowerCase();
    const h = haystack.toLowerCase();
    if (h === n) return 100;
    if (h.startsWith(n)) return 60;
    if (h.includes(n)) return 40;
    if (h.split(/\s+/).some((w) => w.startsWith(n))) return 30;
    return 0;
  }

  type ProjectHit = { kind: "project"; project: SurfaceProject; score: number };
  type TaskHit = { kind: "task"; task: SurfaceTask; project: SurfaceProject | undefined; score: number };
  type Hit = ProjectHit | TaskHit;

  const results = $derived(() => {
    const q = query.trim();
    if (q.length < 2) return [] as Hit[];
    const projectMap = new Map(data.projects.map((p) => [p.slug, p]));
    const projectHits: Hit[] = data.projects
      .map<Hit>((p) => ({ kind: "project", project: p, score: score(q, p.title) }))
      .filter((h) => h.score > 0);
    const taskHits: Hit[] = data.tasks
      .filter((t) => t.state === "open")
      .map<Hit>((t) => ({
        kind: "task",
        task: t,
        project: t.project_slug ? projectMap.get(t.project_slug) : undefined,
        score: score(q, t.text),
      }))
      .filter((h) => h.score > 0);
    return [...projectHits, ...taskHits].sort((a, b) => b.score - a.score).slice(0, 30);
  });

  function clearQuery() {
    query = "";
  }
</script>

<div class="page">
  <header class="hero">
    <h1>Search</h1>
    <div class="search-field">
      <svg class="magnifier" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
        <path d="M7 2a5 5 0 0 1 4 8.05l3.4 3.4a.75.75 0 0 1-1.05 1.06l-3.4-3.4A5 5 0 1 1 7 2Zm0 1.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z" fill="currentColor"/>
      </svg>
      <input
        type="search"
        bind:value={query}
        placeholder="Find a project or task"
        autocapitalize="off"
        autocorrect="off"
      />
      {#if query.length > 0}
        <button type="button" class="clear-btn" onclick={clearQuery} aria-label="Clear search">
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <path d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM5.7 5.7a.75.75 0 0 1 1.06 0L8 6.94l1.24-1.24a.75.75 0 0 1 1.06 1.06L9.06 8l1.24 1.24a.75.75 0 0 1-1.06 1.06L8 9.06 6.76 10.3a.75.75 0 0 1-1.06-1.06L6.94 8 5.7 6.76a.75.75 0 0 1 0-1.06Z" fill="currentColor"/>
          </svg>
        </button>
      {/if}
    </div>
  </header>

  {#if query.trim().length < 2}
    <p class="empty muted">Type two or more characters.</p>
  {:else if results().length === 0}
    <p class="empty muted">No matches for <strong>{query}</strong>.</p>
  {:else}
    <ul class="grouped-list">
      {#each results() as r, i (i)}
        {#if r.kind === "project"}
          <li>
            <button type="button" class="row" onclick={() => onproject(r.project)}>
              <span class="kind project-kind">Project</span>
              <span class="title">{r.project.title}</span>
              <span class="faint">{r.project.open_count} open</span>
            </button>
          </li>
        {:else}
          <li class="task-row">
            <div class="task-head">
              <span class="kind task-kind">Task</span>
              <SourceBadges task={r.task} />
            </div>
            <span class="text">{r.task.text}</span>
            {#if r.project}
              <button type="button" class="proj-link" onclick={() => onproject(r.project!)}>
                {r.project.title}
              </button>
            {:else if r.task.file}
              <span class="faint">{r.task.file}</span>
            {:else if r.task.fingerprint.startsWith("gt:")}
              <span class="faint">Google Tasks</span>
            {/if}
          </li>
        {/if}
      {/each}
    </ul>
  {/if}
</div>

<style>
  .page { padding: 8px 16px 140px; max-width: 720px; margin: 0 auto; }
  .hero { padding: 12px 4px 14px; display: flex; flex-direction: column; gap: 12px; }
  h1 {
    font-size: 34px;
    line-height: 1.05;
    margin: 0;
    font-weight: 700;
    letter-spacing: -0.025em;
  }

  /* iOS search field — rounded pill with embedded magnifier + clear */
  .search-field {
    position: relative;
    display: flex;
    align-items: center;
    background: var(--bg-elev);
    border-radius: 10px;
    padding: 0 10px 0 32px;
  }
  .magnifier {
    position: absolute;
    left: 10px;
    color: var(--fg-faint);
    pointer-events: none;
  }
  .search-field input {
    flex: 1;
    background: transparent;
    border: 0;
    border-radius: 0;
    padding: 10px 0;
    font-size: 17px;
    width: 100%;
    color: var(--fg);
  }
  .search-field input::placeholder { color: var(--fg-faint); }
  .search-field input:focus { outline: none; }
  .clear-btn {
    color: var(--fg-faint);
    padding: 4px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .clear-btn:active { color: var(--fg-dim); }

  .empty { padding: 20px 4px; font-size: 15px; }

  .grouped-list {
    list-style: none;
    padding: 0;
    margin: 0;
    background: var(--bg-elev);
    border-radius: 12px;
    overflow: hidden;
  }
  .grouped-list li {
    border-bottom: 0.5px solid var(--separator);
  }
  .grouped-list li:last-child { border-bottom: 0; }

  .row {
    display: flex;
    width: 100%;
    text-align: left;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
  }
  .row:active { background: var(--tap); }

  .task-row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 12px 14px;
  }
  .kind {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 2px 7px;
    border-radius: 5px;
  }
  .project-kind { background: rgba(191, 90, 242, 0.20); color: #d8b4fe; }
  .task-kind { background: rgba(48, 209, 88, 0.20); color: #86efac; }
  .title { flex: 1; min-width: 0; font-weight: 500; font-size: 16px; letter-spacing: -0.005em; }
  .text { line-height: 1.32; word-wrap: break-word; font-size: 16px; }
  .task-head { display: flex; align-items: center; gap: 8px; }
  .proj-link {
    color: var(--accent);
    font-size: 13px;
    font-weight: 500;
    padding: 0;
    align-self: flex-start;
  }
</style>
